import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText,
  Eye,
  Download,
  Trash2,
  Loader2,
  Send,
  User,
  Building2,
  ArrowRight,
  Mail,
  Receipt,
  Lock,
  AlertTriangle,
  Package,
  Bell,
  CheckCircle2,
  Layers,
  XCircle,
} from "lucide-react";
import { generateInvoicePDF, generateInvoicePDFBlob } from "@/utils/pdfGenerator";
import EntityHistory from "@/components/EntityHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceGenerator = () => {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [resourceMappings, setResourceMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [emailForm, setEmailForm] = useState({ recipient_email: "", subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [lockedBatch, setLockedBatch] = useState(null);
  const [lockedBatches, setLockedBatches] = useState([]);
  const [activeBatchIndex, setActiveBatchIndex] = useState(0);
  const [approvals, setApprovals] = useState(null);
  const [mismatchPreview, setMismatchPreview] = useState(null);
  const [showMismatchDialog, setShowMismatchDialog] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // Bulk Generate state
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkTask, setBulkTask] = useState("");
  const [bulkApplyTds, setBulkApplyTds] = useState(true);
  const [bulkTdsPct, setBulkTdsPct] = useState("1");
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoadingPreview, setBulkLoadingPreview] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkSendingEmails, setBulkSendingEmails] = useState(false);
  const [bulkEmailResults, setBulkEmailResults] = useState(null);

  const [formData, setFormData] = useState({
    resource: "",
    task: "",
    applyRate: "",
    rateType: "per_unit",
    invoiceName: "",
    tdsPercentage: "1",
    applyTds: true,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, invoicesRes, settingsRes, mappingsRes, batchRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/invoices`).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/resource-mappings`).catch(() => ({ data: { mappings: [] } })),
        axios.get(`${API}/batches/locked`).catch(() => ({ data: { batches: [] } })),
      ]);
      setStats(statsRes.data);
      setInvoices(invoicesRes.data.invoices || []);
      setSettings(settingsRes.data);
      setResourceMappings(mappingsRes.data.mappings || []);
      const allBatches = batchRes.data.batches || [];
      setLockedBatches(allBatches);
      setLockedBatch(allBatches[0] || batchRes.data.batch || null);
      setActiveBatchIndex(0);
      try {
        const approvalsRes = await axios.get(`${API}/batch-approvals`);
        setApprovals(approvalsRes.data);
      } catch { setApprovals(null); }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getDisplayName = (email) => {
    const m = resourceMappings.find(r => r.email === email);
    return m?.display_name || email;
  };

  const getPersonalEmail = (email) => {
    const m = resourceMappings.find(r => r.email === email);
    return m?.personal_email || email;
  };

  const handleGenerate = async () => {
    if (!formData.resource) { toast.error("Select a resource"); return; }

    // Preflight mismatch check — only when generating from a locked batch
    if (lockedBatch?.id) {
      setPreviewing(true);
      try {
        const preview = await axios.post(`${API}/invoices/preview-mismatches`, {
          resource: formData.resource,
          task: formData.task || null,
          batch_id: lockedBatch.id,
        });
        const p = preview.data;
        if (p.has_locked_snapshot && p.drift_count > 0) {
          setMismatchPreview(p);
          setShowMismatchDialog(true);
          return; // wait for user resolution
        }
      } catch (err) {
        // Non-fatal — fall through to generate
        console.warn("Mismatch preview failed:", err);
      } finally {
        setPreviewing(false);
      }
    }

    await actuallyGenerate("use_current");
  };

  const actuallyGenerate = async (resolution) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/invoices/generate`, {
        resource: formData.resource,
        task: formData.task || null,
        invoice_name: formData.invoiceName || null,
        tds_percentage: parseFloat(formData.tdsPercentage) || 0,
        apply_tds: formData.applyTds,
        batch_id: lockedBatch?.id || null,
        mismatch_resolution: resolution || "use_current",
      });
      if (res.data.success) {
        const invoice = res.data.invoice;
        toast.success("Invoice generated");
        setSelectedInvoice(invoice);
        setShowPreview(true);
        fetchData();
        
        // Generate and upload PDF to server for email attachment
        try {
          const pdfBlob = generateInvoicePDFBlob(invoice, settings);
          const formDataPdf = new FormData();
          formDataPdf.append("file", pdfBlob, `invoice_${invoice.invoice_number}.pdf`);
          await axios.post(`${API}/invoices/${invoice.id}/upload-pdf`, formDataPdf, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        } catch (uploadErr) {
          console.error("Failed to upload PDF:", uploadErr);
          // Non-critical - email will fall back to server-generated PDF
        }
        
        if (res.data.exceptions?.length > 0) {
          toast.info(`${res.data.exceptions.length} tasks excluded (missing rate/duration)`);
        }
      } else {
        toast.error(res.data.message || "Failed to generate");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success("Invoice deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const resolveMismatch = async (resolution) => {
    setShowMismatchDialog(false);
    await actuallyGenerate(resolution);
    setMismatchPreview(null);
  };

  const openEmailDialog = (invoice) => {
    const personalEmail = getPersonalEmail(invoice.resource_name);
    setEmailInvoice(invoice);
    setEmailForm({
      recipient_email: personalEmail,
      subject: `Invoice ${invoice.invoice_number} - ${settings?.company_name || "GRAPHIKPRO"}`,
      message: "",
    });
    setShowEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) { toast.error("Enter recipient email"); return; }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/invoices/${emailInvoice.id}/email`, {
        invoice_id: emailInvoice.id,
        recipient_email: emailForm.recipient_email,
        subject: emailForm.subject,
        message: emailForm.message,
      });
      toast.success(`Invoice sent to ${emailForm.recipient_email}`);
      setShowEmailDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleExportPDF = (invoice) => {
    generateInvoicePDF(invoice, settings);
    toast.success("PDF downloaded");
  };

  // Get resources filtered by locked batch
  const availableResources = lockedBatch?.resources || stats?.unique_resources || [];
  const availableTaskTypes = lockedBatch?.task_types || stats?.unique_tasks || [];

  // ── Bulk operations ──
  const openBulkDialog = () => {
    setBulkSelected([]);
    setBulkTask("");
    setBulkApplyTds(true);
    setBulkTdsPct("1");
    setBulkPreview(null);
    setBulkResults(null);
    setBulkEmailResults(null);
    setShowBulkDialog(true);
  };

  const toggleBulkResource = (r) => {
    setBulkSelected((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  };

  const fetchBulkPreview = async () => {
    if (bulkSelected.length === 0) { setBulkPreview(null); return; }
    setBulkLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/invoices/bulk-preview`, {
        items: bulkSelected.map((r) => ({ resource: r, task: bulkTask || null })),
        batch_id: lockedBatch?.id || null,
        tds_percentage: parseFloat(bulkTdsPct) || 0,
        apply_tds: bulkApplyTds,
      });
      setBulkPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBulkLoadingPreview(false);
    }
  };

  // Auto-refresh preview when selection or TDS changes
  useEffect(() => {
    if (showBulkDialog && bulkSelected.length > 0) {
      const t = setTimeout(fetchBulkPreview, 250);
      return () => clearTimeout(t);
    }
    if (bulkSelected.length === 0) setBulkPreview(null);
    // eslint-disable-next-line
  }, [showBulkDialog, bulkSelected, bulkTask, bulkApplyTds, bulkTdsPct]);

  const handleBulkGenerate = async () => {
    if (bulkSelected.length === 0) { toast.error("Select at least one resource"); return; }
    setBulkGenerating(true);
    setBulkResults(null);
    setBulkEmailResults(null);
    try {
      const res = await axios.post(`${API}/invoices/bulk-generate`, {
        items: bulkSelected.map((r) => ({ resource: r, task: bulkTask || null })),
        batch_id: lockedBatch?.id || null,
        tds_percentage: parseFloat(bulkTdsPct) || 0,
        apply_tds: bulkApplyTds,
        mismatch_resolution: "use_current",
      });
      setBulkResults(res.data);
      const { generated, failed } = res.data;
      if (failed === 0) toast.success(`Generated ${generated} invoice(s)`);
      else toast.warning(`Generated ${generated}, ${failed} failed`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk generation failed");
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleBulkSendEmails = async () => {
    if (!bulkResults) return;
    const ids = bulkResults.results.filter((r) => r.ok && r.invoice_id).map((r) => r.invoice_id);
    if (ids.length === 0) { toast.error("No invoices to email"); return; }
    setBulkSendingEmails(true);
    try {
      const res = await axios.post(`${API}/invoices/bulk-email`, {
        invoice_ids: ids,
      });
      setBulkEmailResults(res.data);
      const { sent, failed } = res.data;
      if (failed === 0) toast.success(`Emailed ${sent} invoice(s)`);
      else toast.warning(`Emailed ${sent}, ${failed} failed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk email failed");
    } finally {
      setBulkSendingEmails(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="invoice-generator-page">
      <div className="page-header flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="page-title">Invoice Generator</h1>
          <p className="page-subtitle">Generate and send invoices for completed tasks</p>
        </div>
        <Button
          onClick={() => openBulkDialog()}
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          data-testid="bulk-generate-btn"
        >
          <Layers className="w-4 h-4" />
          Bulk Generate
        </Button>
      </div>

      {/* Batch Status Banner */}
      {lockedBatches.length > 0 ? (
        <div className="mb-5 space-y-2">
          {/* Batch selector tabs when multiple batches */}
          {lockedBatches.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="batch-selector">
              {lockedBatches.map((b, i) => (
                <button
                  key={b.id}
                  onClick={() => { setActiveBatchIndex(i); setLockedBatch(b); }}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-xs font-medium transition-all ${
                    i === activeBatchIndex
                      ? "bg-emerald-50 border-emerald-300 text-emerald-800 shadow-sm"
                      : "bg-white border-slate-200 text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/50"
                  }`}
                  data-testid={`batch-tab-${i}`}
                >
                  <Lock className="w-3 h-3" />
                  <span className="font-semibold">{b.name}</span>
                  <span className="text-[10px] opacity-70">{b.task_count} tasks</span>
                  <span className="text-[10px] font-mono opacity-70">₹{(b.total_amount || 0).toLocaleString("en-IN")}</span>
                </button>
              ))}
            </div>
          )}

          {/* Active batch banner */}
          <div className="flex items-center gap-3 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="locked-batch-banner">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-emerald-800">{lockedBatch.name}</p>
              <p className="text-xs text-emerald-600">
                {lockedBatch.billing_month || lockedBatch.month} &middot; {lockedBatch.resources?.length || 0} resources &middot; {lockedBatch.task_count || 0} tasks &middot; ₹{(lockedBatch.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0 })}
              </p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] font-semibold">ACTIVE BATCH</Badge>
          </div>
        </div>
      ) : (
        <div className="mb-5 flex items-center gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-xl" data-testid="no-batch-banner">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">No locked batch</p>
            <p className="text-xs text-amber-600">Lock a batch in Invoice Batches to filter resources. Showing all resources.</p>
          </div>
        </div>
      )}

      {/* Approval Notifications Panel */}
      {approvals && approvals.approvals && (approvals.approved_count > 0 || approvals.disputed_count > 0) && (
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Bell className="w-4 h-4 text-blue-600" />
                Resource Responses
                {approvals.approved_count > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{approvals.approved_count} approved</Badge>}
                {approvals.disputed_count > 0 && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">{approvals.disputed_count} disputed</Badge>}
              </h3>
            </div>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {(approvals.approvals || []).map((a, i) => (
                <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${a.disputed ? "bg-red-50 border border-red-100" : a.approved ? "bg-emerald-50 border border-emerald-100" : "bg-slate-50"}`}>
                  {a.approved && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                  {a.disputed && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700">{a.resource_name} <span className="text-slate-400 font-normal">({a.translator_email})</span></p>
                    <p className="text-[10px] text-slate-500">{a.batch_name} {a.approved ? `— Approved ${new Date(a.approved_at).toLocaleDateString()}` : ""}</p>
                    {a.disputed && a.dispute_message && <p className="text-[10px] text-red-600 mt-0.5">Dispute: {a.dispute_message}</p>}
                  </div>
                  {a.approved && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">Ready to Invoice</Badge>}
                  {a.disputed && <Badge className="bg-red-100 text-red-700 border-0 text-[9px]">Needs Review</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compact Generation Form — Single Row on desktop, stacked on mobile */}
      <Card className="card mb-6">
        <CardContent className="p-4 sm:p-5">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-4 md:items-end">
            {/* Resource */}
            <div className="col-span-2 md:col-span-3 space-y-1.5">
              <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Resource *</Label>
              <Select value={formData.resource} onValueChange={(v) => setFormData({ ...formData, resource: v })}>
                <SelectTrigger data-testid="select-resource" className="h-10 md:h-9">
                  <SelectValue placeholder="Select resource" />
                </SelectTrigger>
                <SelectContent>
                  {availableResources.map((r, i) => (
                    <SelectItem key={i} value={r}>
                      <div className="flex flex-col leading-tight py-0.5">
                        <span className="text-sm font-medium">{getDisplayName(r)}</span>
                        <span className="text-[10px] text-slate-400 font-mono">{r}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Task */}
            <div className="col-span-2 md:col-span-2 space-y-1.5">
              <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Task</Label>
              <Select value={formData.task || "all"} onValueChange={(v) => setFormData({ ...formData, task: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="select-task" className="h-10 md:h-9">
                  <SelectValue placeholder="All tasks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  {availableTaskTypes.map((t, i) => (
                    <SelectItem key={i} value={t || `task-${i}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* TDS */}
            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">TDS %</Label>
              <div className="flex items-center gap-1.5">
                <Checkbox
                  checked={formData.applyTds}
                  onCheckedChange={(v) => setFormData({ ...formData, applyTds: v })}
                  data-testid="tds-checkbox"
                  className="h-5 w-5 md:h-4 md:w-4"
                />
                <Input
                  type="number"
                  step="0.1"
                  value={formData.tdsPercentage}
                  onChange={(e) => setFormData({ ...formData, tdsPercentage: e.target.value })}
                  className="h-10 md:h-9 w-full md:w-16"
                  disabled={!formData.applyTds}
                  data-testid="input-tds"
                />
              </div>
            </div>

            {/* Invoice Name */}
            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Invoice Label</Label>
              <Input
                placeholder="e.g., Jan 2026"
                value={formData.invoiceName}
                onChange={(e) => setFormData({ ...formData, invoiceName: e.target.value })}
                className="h-10 md:h-9"
                data-testid="input-invoice-name"
              />
            </div>

            {/* Generate Button */}
            <div className="col-span-2 md:col-span-1">
              <Button
                onClick={handleGenerate}
                disabled={generating || previewing || !formData.resource}
                className="btn-accent w-full h-10 md:h-9"
                data-testid="generate-invoice-btn"
              >
                {generating || previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <span className="flex items-center gap-2 md:gap-0">
                    <Receipt className="w-4 h-4" />
                    <span className="md:hidden text-sm font-medium">Generate Invoice</span>
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Billing context line */}
          {formData.resource && (
            <div className="mt-3 flex items-start sm:items-center gap-x-2 gap-y-1.5 text-xs text-slate-500 flex-wrap">
              <div className="inline-flex items-center gap-1.5 flex-wrap">
                <User className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium text-slate-700">From:</span>
                <span>{getDisplayName(formData.resource)}</span>
                <span className="text-slate-300 font-mono text-[10px] hidden sm:inline">&lt;{formData.resource}&gt;</span>
              </div>
              <span className="hidden sm:inline"><ArrowRight className="w-3 h-3 text-slate-400" /></span>
              <div className="inline-flex items-center gap-1.5 flex-wrap">
                <Building2 className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium text-slate-700">Bill To:</span>
                <span>{settings?.company_name || "GRAPHIKPRO"}</span>
              </div>
              {(() => {
                const personal = getPersonalEmail(formData.resource);
                if (personal && personal !== formData.resource) {
                  return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 max-w-full break-all">
                      <Mail className="w-3 h-3 shrink-0" />
                      <span className="text-[10px]">Email → {personal}</span>
                    </span>
                  );
                }
                return (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 max-w-full break-all" title="No personal email set — invoice emails will go to the work email.">
                    <Mail className="w-3 h-3 shrink-0" />
                    <span className="text-[10px]">Email → {formData.resource} (no personal email)</span>
                  </span>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card className="card">
        <CardHeader className="card-header flex-row items-center justify-between">
          <CardTitle className="card-title flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" />
            Generated Invoices ({invoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {invoices.length > 0 ? (
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Invoice #</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="text-center w-[60px]">Items</TableHead>
                    <TableHead className="text-right w-[100px]">Subtotal</TableHead>
                    <TableHead className="text-right w-[80px]">TDS</TableHead>
                    <TableHead className="text-right w-[110px]">Total</TableHead>
                    <TableHead className="text-center w-[80px]">Status</TableHead>
                    <TableHead className="w-[130px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} className="table-row-hover">
                      <TableCell>
                        <div>
                          <span className="font-mono text-xs text-slate-600">{inv.invoice_number}</span>
                          <p className="text-[11px] text-slate-400">{inv.invoice_date?.substring(0, 10)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-slate-800">{inv.resource_display_name || inv.resource_name}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">{inv.invoice_name || "-"}</span>
                      </TableCell>
                      <TableCell className="text-center font-tabular">
                        {inv.line_items?.length || 0}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-slate-600 font-tabular">
                        ₹{inv.subtotal?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-tabular">
                        {inv.tds_amount > 0 ? (
                          <span className="text-red-500">-₹{inv.tds_amount?.toFixed(2)}</span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold text-emerald-600 font-tabular">
                        ₹{inv.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-[10px] border-0 capitalize ${
                          inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {inv.status || "generated"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setSelectedInvoice(inv); setShowPreview(true); }} data-testid={`view-invoice-${inv.id}`}>
                            <Eye className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleExportPDF(inv)} data-testid={`download-invoice-${inv.id}`}>
                            <Download className="w-3.5 h-3.5 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEmailDialog(inv)} data-testid={`email-invoice-${inv.id}`}>
                            <Send className="w-3.5 h-3.5 text-violet-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(inv.id)} data-testid={`delete-invoice-${inv.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FileText className="w-7 h-7 text-slate-400" />
              </div>
              <p className="empty-state-title">No Invoices Yet</p>
              <p className="empty-state-text">Select a resource above and generate your first invoice.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" aria-describedby="preview-desc">
          <DialogHeader>
            <DialogTitle>Invoice Preview</DialogTitle>
            <p id="preview-desc" className="text-sm text-slate-500">Review the generated invoice</p>
          </DialogHeader>

          {selectedInvoice && (
            <div className="invoice-print">
              <div className="invoice-header">
                <div>
                  <h1 className="invoice-title">{selectedInvoice.resource_display_name || selectedInvoice.resource_name}</h1>
                  <p className="text-xs text-slate-400 uppercase tracking-wide mt-1">Invoice From</p>
                </div>
                <div className="invoice-meta">
                  <p className="text-xs uppercase tracking-wider text-slate-400">Invoice</p>
                  <p className="text-base font-bold text-slate-900 font-mono">{selectedInvoice.invoice_number}</p>
                  <p className="text-sm text-slate-500 mt-1">{selectedInvoice.invoice_date?.substring(0, 10)}</p>
                </div>
              </div>

              {selectedInvoice.invoice_name && (
                <div className="mb-4 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm font-semibold text-blue-900">{selectedInvoice.invoice_name}</p>
                </div>
              )}

              <div className="mb-5 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Bill To</p>
                <p className="font-semibold text-slate-900">{selectedInvoice.bill_to || settings?.company_name || "GRAPHIKPRO"}</p>
              </div>

              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Main Title</th>
                    <th>Episode Title</th>
                    <th className="text-center">Ep #</th>
                    <th className="text-center">Dur/Words</th>
                    <th className="text-right">Rate</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.line_items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="text-sm">{item.main_title || "-"}</td>
                      <td className="text-sm text-slate-600">{item.episode_title || "-"}</td>
                      <td className="text-center text-sm">{item.episode_number || "-"}</td>
                      <td className="text-center text-sm text-slate-500">{item.duration_words ?? "-"}</td>
                      <td className="text-right text-sm font-mono">
                        {item.rate_type === "flat" ? "Flat" : `₹${item.rate?.toFixed(2)}`}
                      </td>
                      <td className="text-right text-sm font-mono font-medium">₹{item.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="invoice-total">
                <div className="flex justify-end gap-8">
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Subtotal</p>
                    <p className="text-base font-semibold text-slate-900">₹{selectedInvoice.subtotal?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  {selectedInvoice.tds_amount > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-red-500">TDS ({selectedInvoice.tds_percentage || 1}%)</p>
                      <p className="text-base font-semibold text-red-600">-₹{selectedInvoice.tds_amount?.toFixed(2)}</p>
                    </div>
                  )}
                  <div className="text-right pl-6 border-l border-slate-200">
                    <p className="text-xs text-slate-500">Grand Total</p>
                    <p className="text-xl font-bold text-emerald-600">₹{selectedInvoice.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3 no-print">
            <Button variant="outline" size="sm" onClick={() => selectedInvoice && openEmailDialog(selectedInvoice)} data-testid="preview-send-email-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send Email
            </Button>
            <Button size="sm" onClick={() => selectedInvoice && handleExportPDF(selectedInvoice)} className="btn-accent" data-testid="preview-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
            </Button>
          </div>

          {/* Inline change history for this invoice */}
          {selectedInvoice && (
            <div className="mt-4 no-print">
              <EntityHistory collection="invoices" entityId={selectedInvoice.id} title="Invoice History" onChanged={() => fetchData()} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Generate Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              Bulk Generate Invoices
              {lockedBatch && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Batch: {lockedBatch.name}</Badge>}
            </DialogTitle>
            <p className="text-xs text-slate-500">Pick resources, set TDS, review the totals, then generate. You decide when to send emails.</p>
          </DialogHeader>

          <div className="flex-1 overflow-auto pr-1 space-y-4">
            {/* Step 1 — Top controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase text-slate-500">Task (optional)</Label>
                <Select value={bulkTask || "all"} onValueChange={(v) => setBulkTask(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9" data-testid="bulk-task-select"><SelectValue placeholder="All tasks" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tasks</SelectItem>
                    {availableTaskTypes.map((t, i) => (<SelectItem key={i} value={t || `task-${i}`}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase text-slate-500">Apply TDS</Label>
                <div className="h-9 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5">
                  <Checkbox
                    checked={bulkApplyTds}
                    onCheckedChange={(v) => setBulkApplyTds(!!v)}
                    data-testid="bulk-tds-checkbox"
                  />
                  <span className="text-sm text-slate-600">{bulkApplyTds ? "TDS will be deducted" : "No TDS"}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase text-slate-500">TDS %</Label>
                <Input
                  type="number" step="0.1"
                  value={bulkTdsPct}
                  onChange={(e) => setBulkTdsPct(e.target.value)}
                  disabled={!bulkApplyTds}
                  className="h-9"
                  data-testid="bulk-tds-pct"
                />
              </div>
            </div>

            {/* Step 2 — Resource picker */}
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 flex items-center gap-2 border-b border-slate-200 sticky top-0 z-10">
                <Checkbox
                  checked={bulkSelected.length > 0 && bulkSelected.length === availableResources.length}
                  onCheckedChange={(v) => setBulkSelected(v ? [...availableResources] : [])}
                  data-testid="bulk-select-all"
                />
                <span className="text-xs text-slate-600 font-medium">
                  {bulkSelected.length} of {availableResources.length} selected
                </span>
                {bulkSelected.length > 0 && (
                  <Button variant="ghost" size="sm" className="ml-auto h-6 text-[10px] text-slate-500" onClick={() => setBulkSelected([])} data-testid="bulk-clear">Clear</Button>
                )}
              </div>
              <div className="max-h-56 overflow-auto">
                {availableResources.map((r) => {
                  const isOn = bulkSelected.includes(r);
                  const row = bulkPreview?.rows?.find((x) => x.resource === r);
                  return (
                    <label
                      key={r}
                      className={`flex items-center gap-3 px-3 py-2 text-xs cursor-pointer border-b border-slate-100 last:border-b-0 ${isOn ? "bg-blue-50/50" : "hover:bg-slate-50"}`}
                      data-testid={`bulk-resource-${r}`}
                    >
                      <Checkbox checked={isOn} onCheckedChange={() => toggleBulkResource(r)} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-700 truncate">{getDisplayName(r)}</div>
                        <div className="text-[10px] text-slate-400 font-mono truncate">{r}</div>
                      </div>
                      {row && (
                        <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-500">
                          <span>{row.task_count} tasks</span>
                          <span className="font-mono text-slate-700">₹{(row.grand_total || 0).toLocaleString("en-IN")}</span>
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Step 3 — Preview table */}
            {bulkSelected.length > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600">Preview</span>
                  {bulkLoadingPreview && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="bulk-preview-table">
                    <thead className="bg-white border-b border-slate-200">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-500">Resource</th>
                        <th className="px-3 py-2 text-center text-[10px] uppercase text-slate-500">Tasks</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase text-slate-500">Subtotal</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase text-slate-500">TDS</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase text-slate-500">Grand</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bulkPreview?.rows || []).map((row) => (
                        <tr key={row.resource} className={`border-b border-slate-100 ${row.ready ? "" : "bg-amber-50/40"}`} data-testid={`bulk-preview-row-${row.resource}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-700">{row.display_name}</div>
                            <div className="text-[10px] text-slate-400 font-mono">{row.personal_email}</div>
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">
                            {row.task_count}
                            {row.skipped_count > 0 && <span className="text-amber-600 ml-1">(+{row.skipped_count} skipped)</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-slate-700">₹{(row.subtotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-right font-mono text-amber-700">{bulkApplyTds ? `₹${(row.tds_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">₹{(row.grand_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                    {bulkPreview?.summary && (
                      <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                        <tr className="font-semibold">
                          <td className="px-3 py-2 text-slate-700 uppercase text-[10px]">Total ({bulkPreview.summary.ready_count}/{bulkPreview.summary.resource_count} ready)</td>
                          <td className="px-3 py-2 text-center text-slate-700">{bulkPreview.summary.total_tasks}</td>
                          <td className="px-3 py-2 text-right text-slate-800 font-mono" data-testid="bulk-total-subtotal">₹{(bulkPreview.summary.total_subtotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-right text-amber-700 font-mono" data-testid="bulk-total-tds">{bulkApplyTds ? `₹${(bulkPreview.summary.total_tds || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}</td>
                          <td className="px-3 py-2 text-right text-emerald-700 font-mono" data-testid="bulk-total-grand">₹{(bulkPreview.summary.total_grand || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {/* Generation results */}
            {bulkResults && (
              <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="bulk-results">
                <div className="bg-slate-50 px-3 py-2 border-b flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600">
                    Generation Results — {bulkResults.generated} generated{bulkResults.failed ? `, ${bulkResults.failed} failed` : ""}
                  </span>
                </div>
                <div className="max-h-44 overflow-auto">
                  {bulkResults.results.map((r, i) => (
                    <div key={i} className="px-3 py-1.5 text-[11px] border-b border-slate-100 flex items-center gap-2">
                      {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-600" />}
                      <span className="font-medium text-slate-700 min-w-[150px] truncate">{getDisplayName(r.resource)}</span>
                      {r.ok ? (
                        <>
                          <span className="font-mono text-slate-500">{r.invoice_number}</span>
                          <span className="ml-auto font-mono text-emerald-700 font-semibold">₹{(r.grand_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                        </>
                      ) : (
                        <span className="text-red-600 truncate">{r.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email results */}
            {bulkEmailResults && (
              <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="bulk-email-results">
                <div className="bg-slate-50 px-3 py-2 border-b">
                  <span className="text-xs font-semibold text-slate-600">
                    Email Results — {bulkEmailResults.sent} sent{bulkEmailResults.failed ? `, ${bulkEmailResults.failed} failed` : ""}
                  </span>
                </div>
                <div className="max-h-44 overflow-auto">
                  {bulkEmailResults.results.map((r, i) => (
                    <div key={i} className="px-3 py-1.5 text-[11px] border-b border-slate-100 flex items-center gap-2">
                      {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-600" />}
                      <span className="font-mono text-slate-700">{r.invoice_number || r.invoice_id?.slice(0, 8)}</span>
                      <span className="text-slate-500 truncate">→ {r.to}</span>
                      {!r.ok && <span className="ml-auto text-red-600 truncate text-[10px]">{r.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 pt-3 border-t border-slate-100 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setShowBulkDialog(false)} data-testid="bulk-close-btn">
              Close
            </Button>
            {!bulkResults ? (
              <Button
                size="sm"
                onClick={handleBulkGenerate}
                disabled={bulkSelected.length === 0 || bulkGenerating}
                className="btn-accent"
                data-testid="bulk-generate-confirm-btn"
              >
                {bulkGenerating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
                Generate {bulkSelected.length} Invoice{bulkSelected.length === 1 ? "" : "s"}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleBulkSendEmails}
                disabled={bulkSendingEmails || bulkResults.generated === 0 || !!bulkEmailResults}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="bulk-email-btn"
              >
                {bulkSendingEmails ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                {bulkEmailResults ? "Emails Sent" : `Send ${bulkResults.generated} Email${bulkResults.generated === 1 ? "" : "s"}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mismatch Warning Dialog */}
      <Dialog open={showMismatchDialog} onOpenChange={setShowMismatchDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" aria-describedby="mismatch-desc" data-testid="mismatch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Data has drifted since this batch was locked
            </DialogTitle>
            <p id="mismatch-desc" className="text-sm text-slate-500">
              Some adjustments were edited after locking. Choose which values to use for this invoice.
            </p>
          </DialogHeader>

          {mismatchPreview && (
            <div className="flex-1 overflow-auto space-y-4 pr-1">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Locked Total</p>
                  <p className="text-lg font-semibold text-slate-800 font-mono mt-0.5" data-testid="mismatch-locked-total">
                    ₹{mismatchPreview.locked_total?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Current Total</p>
                  <p className="text-lg font-semibold text-slate-800 font-mono mt-0.5" data-testid="mismatch-current-total">
                    ₹{mismatchPreview.current_total?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={`p-3 rounded-lg border ${Math.abs(mismatchPreview.amount_delta || 0) > 0.01 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Delta</p>
                  <p className={`text-lg font-semibold font-mono mt-0.5 ${(mismatchPreview.amount_delta || 0) > 0 ? "text-emerald-700" : (mismatchPreview.amount_delta || 0) < 0 ? "text-red-600" : "text-slate-800"}`} data-testid="mismatch-delta">
                    {(mismatchPreview.amount_delta || 0) > 0 ? "+" : ""}₹{mismatchPreview.amount_delta?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Drift summary */}
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]" data-testid="mismatch-drift-count">
                  {mismatchPreview.drift_count} task{mismatchPreview.drift_count === 1 ? "" : "s"} changed
                </Badge>
                {mismatchPreview.already_invoiced_count > 0 && (
                  <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">
                    {mismatchPreview.already_invoiced_count} already invoiced
                  </Badge>
                )}
                <span className="text-slate-400">of {mismatchPreview.task_count} total tasks in batch</span>
              </div>

              {/* Drift table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-[320px] overflow-auto">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Title / Episode</TableHead>
                        <TableHead className="w-[100px]">Task</TableHead>
                        <TableHead className="w-[110px]">Field</TableHead>
                        <TableHead>Locked Value</TableHead>
                        <TableHead>Current Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(mismatchPreview.drift || []).flatMap((item, i) =>
                        (item.diffs || []).map((d, j) => (
                          <TableRow key={`${i}-${j}`} data-testid={`mismatch-row-${i}-${j}`}>
                            <TableCell className="text-xs">
                              <div className="font-medium text-slate-800 truncate max-w-[180px]">{item.main_title || "—"}</div>
                              <div className="text-slate-500 truncate max-w-[180px]">{item.episode_title || ""}</div>
                            </TableCell>
                            <TableCell className="text-xs text-slate-600">{item.task || "—"}</TableCell>
                            <TableCell className="text-xs font-mono uppercase text-slate-500">{d.field}</TableCell>
                            <TableCell className="text-xs font-mono text-slate-700">
                              <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
                                {d.locked_value === null || d.locked_value === undefined || d.locked_value === "" ? "—" : String(d.locked_value)}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs font-mono text-slate-700">
                              <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">
                                {d.current_value === null || d.current_value === undefined || d.current_value === "" ? "—" : String(d.current_value)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">
                <strong>Use Locked Snapshot</strong> generates the invoice with the values frozen at lock time (recommended for audit consistency).<br />
                <strong>Use Current Live Data</strong> uses today's edited values — choose this only if the changes are intentional corrections.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 pt-3 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={() => setShowMismatchDialog(false)} data-testid="mismatch-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => resolveMismatch("use_current")}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="mismatch-use-current-btn"
            >
              Use Current Live Data
            </Button>
            <Button
              size="sm"
              onClick={() => resolveMismatch("use_locked")}
              className="btn-accent"
              data-testid="mismatch-use-locked-btn"
            >
              <Lock className="w-3.5 h-3.5 mr-1.5" />
              Use Locked Snapshot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>        <DialogContent className="sm:max-w-md" aria-describedby="email-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Send Invoice</DialogTitle>
            <p id="email-desc" className="text-sm text-slate-500">Send this invoice as a PDF attachment</p>
          </DialogHeader>
          {emailInvoice && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{emailInvoice.invoice_number}</p>
                    <p className="text-xs text-slate-500">{emailInvoice.resource_display_name || emailInvoice.resource_name}</p>
                  </div>
                  <p className="text-sm font-semibold text-emerald-600">₹{emailInvoice.grand_total?.toFixed(2)}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Recipient Email *</Label>
                <Input
                  type="email"
                  value={emailForm.recipient_email}
                  onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="email-recipient-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Subject</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  data-testid="email-subject-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Message (optional)</Label>
                <Textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  placeholder="Add a personal message..."
                  rows={2}
                  data-testid="email-message-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSendEmail} disabled={sendingEmail} className="btn-accent" data-testid="send-email-btn">
              {sendingEmail ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              {sendingEmail ? "Sending..." : "Send Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceGenerator;
